import { createAsyncThunk } from "@reduxjs/toolkit";

import { type UserRoleEnum } from "@fitness-app/data-models";
import { type AutomationFilterCondition } from "@fitness-app/data-models/entities/AutomationFilterCondition";
import { type FileMessageContent } from "@fitness-app/data-models/entities/Chat";

import { type AsyncThunkCreator } from "../../../index";
import { CHAT_REDUCER_NAME } from "../types";

type BulkMessageAttachments = {
  /** Uploaded to `chat/bulk/{uid}/...`; sent with the last message (TRE-2306). */
  attachments?: FileMessageContent[];
  /** Media library videos, resolved server-side like in campaigns. */
  mediaLibraryVideoIds?: string[];
};

export type TaskDate = {
  sendAt: number;
  sendDate: string;
  sentTime: {
    hours: number;
    minutes: number;
  };
};
type Payload =
  | ({
      clients: {
        id: string;
        userId: string;
        email: string;
        firstName: string;
        lastName: string | null;
        assignedTrainerId: string | null;
      }[];
      messages: string[];
      sendToMarketingChannelIfExists?: boolean;
      /** Limited-access clients are dropped server-side unless this is set (TRE-801). */
      includeLimitedAccess?: boolean;
      authorId: string;
      authorRole: UserRoleEnum;
      authorName: string;
      authorAvatar: string | null;
      sendImmediately: boolean;
      dates?: TaskDate[];
      /** Checked for every client when a scheduled message is sent; ignored for immediate sends. */
      filters?: AutomationFilterCondition[];
    } & BulkMessageAttachments)
  | ({
      tags: string[];
      sendToMarketingChannelIfExists?: boolean;
      includeLimitedAccess?: boolean;
      condition: "hasTag" | "notHasTag";
      messages: string[];
      authorId: string;
      authorRole: UserRoleEnum;
      authorName: string;
      authorAvatar: string | null;
      sendImmediately: boolean;
      dates?: TaskDate[];
      /** Checked for every client when a scheduled message is sent; ignored for immediate sends. */
      filters?: AutomationFilterCondition[];
    } & BulkMessageAttachments);

export const sendMessageToMultipleUsers = createAsyncThunk<
  { count: number; clients: number },
  Payload,
  AsyncThunkCreator<string>
>(`${CHAT_REDUCER_NAME}/sendMessageToMultipleUsers`, async (payload, { extra: { chatApi } }) => {
  if (!chatApi) {
    throw new Error("chatApi is not defined");
  }

  const response = await chatApi.post<{ count: number; clients: number }>("/send-message-to-multiple-users", payload);

  return response.data;
});
